import * as React from 'react';
import classnames from 'classnames';
import { DayData, Slot } from 'booking/lib/types';
import { useIntl } from 'react-intl';
import CircularProgress from '@mui/material/CircularProgress';
import { SelectedSlot } from 'booking/components/NearbyLocationsTimeSlots/NearbyLocation';
import ConfirmModal from 'booking/components/ConfirmModal';
import useIsClosed from 'booking/hooks/useIsClosed';
import useIsToday from 'booking/hooks/useIsToday';
import { THRESHOLD_LOW_AVAILABILITY } from 'booking/lib/utils';
import useLocationTimeSlots from 'booking/hooks/useTimeSlots';
import { REPLACE_DR_PREFIX_REGEX } from 'booking/lib/constants';
import NoAvailabilityDateForm from 'booking/components/NoAvailabilityDateForm';
import NearbySlotsProvider from 'booking/components/NearbySlotsProvider';
import Day from 'booking/components/DateForm/Day';
import Provider from 'booking/components/DateForm/Provider';
import { LocationWithGalleryImages } from 'locations/lib/types';
import ChevronRight from 'booking/components/DateForm/assets/chevron-right.inline.svg';
import ChevronLeft from 'booking/components/DateForm/assets/chevron-left.inline.svg';
import useStartBookingFlow from 'booking/hooks/useStartBookingFlow';
import isEqual from 'lodash/isEqual';
import useScrollControl from 'core/hooks/useScrollControl';
import useExcludedVetspireProviderIds from 'booking/hooks/useExcludedVetspireProviderIds';
import { LowAvailabilityDateFormProps } from 'booking/components/NoAvailabilityDateForm/NoAvailabilityDateForm';
import { useShouldShowDigitalWalkIn } from 'booking/hooks/useShouldShowDigitalWalkIn';
import { useDigitalWalkInAvailability } from 'booking/hooks/useDigitalWalkInAvailability';
import DigitalWalkInModal from 'booking/components/DigitalWalkInModal';
import DigitalWalkInBooking from 'booking/components/DigitalWalkInBooking';
import * as styles from './LocationBookingCalendar.module.scss';

function SlotButtons({
    slots,
    selectedSlot,
    onSelect,
}: {
    slots: readonly Slot[] | null;
    selectedSlot?: string;
    onSelect: (time: string) => void;
}) {
    return (
        <div className={styles.slotsButtons}>
            {slots?.map(({ time, label }) => (
                <button
                    type="button"
                    onClick={() => onSelect(time)}
                    key={time}
                    className={classnames({
                        [styles.isSelected]: selectedSlot === time,
                    })}
                >
                    {label}
                </button>
            ))}
        </div>
    );
}

function NoAvailability({
    location,
    ungroupedSlots,
    selectSlotAndGoNext,
    selectedSlot,
    selectedDate,
    setSelectedNearbySlot,
    isToday,
    isClosed,
    showHeader,
    shouldDisplayWalkInComponent,
    setDigitalWalkInModalOpen,
}: Omit<LowAvailabilityDateFormProps, 'isMobile' | 'eventPrefix'>) {
    return (
        <NoAvailabilityDateForm
            location={location}
            ungroupedSlots={ungroupedSlots}
            selectSlotAndGoNext={selectSlotAndGoNext}
            selectedSlot={selectedSlot ?? ''}
            selectedDate={selectedDate}
            setSelectedNearbySlot={setSelectedNearbySlot}
            showHeader={showHeader}
            isToday={isToday}
            isClosed={isClosed}
            isMobile={true}
            shouldDisplayWalkInComponent={shouldDisplayWalkInComponent}
            setDigitalWalkInModalOpen={setDigitalWalkInModalOpen}
            eventPrefix="cdp"
            classes={{
                slots: classnames(styles.slots, styles.availabilitySlots),
                placeholder: styles.closedPlaceholder,
                slotsButtons: styles.nearbySlotsButtons,
                nearbyLocations: styles.nearbyLocations,
                nearbyClinicWrapper: styles.nearbyClinicWrapper,
                clinicName: styles.nearbyClinicName,
            }}
        />
    );
}

export type LocationBookingCalendarProps = {
    location: LocationWithGalleryImages;
    onLoadingChange?: (isLoading: boolean) => void;
};

export default function LocationBookingCalendar({
    location,
    onLoadingChange,
}: LocationBookingCalendarProps): React.ReactElement {
    const startBooking = useStartBookingFlow();
    const calendar = React.useRef<HTMLDivElement>(null);
    const citySlug = location.city?.slug ?? ''; // we know that the city has a slug
    // we know that the location has a shortSlug, because it is a calculated field, base
    // on location.slug
    const locationSlug = location.shortSlug ?? '';
    const [selectedNearbySlot, setSelectedNearbySlot] =
        React.useState<SelectedSlot | null>(null);
    const {
        ref: providersRef,
        scroll,
        scrollToLast,
        scrollToNext,
        isAtStart,
        isAtEnd,
    } = useScrollControl('horizontal', 44);
    const excludedVetspireProviderIds = useExcludedVetspireProviderIds();
    const wrapperRef = React.useRef<HTMLDivElement>(null);
    const [lastWeekDays, setLastWeekDays] = React.useState<
        ReadonlyArray<DayData | null>
    >([]);

    const locationId = location._id;

    const allowIndividualProviderBookings =
        location?.bookingSettings?.allowIndividualProviderBookings;

    const nearbyLocations = React.useMemo(
        () =>
            location?.nearestLocations?.map(
                (nearLocation) => nearLocation.location,
            ) ?? null,
        [location?.nearestLocations],
    );

    const {
        week,
        weekControl: {
            next: nextWeek,
            previous: previousWeek,
            hasNext: hasNextWeek,
            hasPrevious: hasPreviousWeek,
        },
        daysOfTheWeek,
        selectDate,
        selectedDate,
        provider,
        setProvider,
        weekProviders: providers,
        weekDays,
        allSlots,
        ungroupedSlots,
        selectSlot,
        selectedSlot,
        isNextWeek,
        isLoadingDates,
        isLoadingSlots,
        selectedDateMMMMDYYYY,
        todayYYYYMMDD,
        timezone,
    } = useLocationTimeSlots({
        locationId,
        locationOpenDate: location.openDate,
        useWeeklySchedule: true,
    });

    // if a provider was previously selected from another location, reset the assignment
    React.useEffect(() => {
        if (provider && providers && 'id' in provider) {
            const index = providers.findIndex(
                (p) => p && 'id' in p && p.id === provider.id,
            );
            if (index === -1) {
                setProvider(null);
            }
        }
    }, [provider, providers, setProvider]);

    const fullyBooked = allSlots?.length === 0;

    const filteredOutPlaceholdersFromProviders = React.useMemo(() => {
        return providers?.filter(
            (providerToFilter) =>
                !excludedVetspireProviderIds.includes(providerToFilter.id),
        );
    }, [providers, excludedVetspireProviderIds]);
    const isToday = useIsToday(timezone, selectedDate);

    const isThisWeek = React.useMemo(
        () => !!weekDays.find((day) => day?.date === todayYYYYMMDD),
        [weekDays, todayYYYYMMDD],
    );
    const locationIdArray = React.useMemo(
        () => (locationId ? [locationId] : []),
        [locationId],
    );

    const isClosed = useIsClosed(locationIdArray, isToday, selectedDate, [
        location,
    ]);

    React.useEffect(() => {
        let handle: number | undefined;

        const handler = () => {
            if (calendar.current) {
                handle = requestAnimationFrame(() => {
                    handle = requestAnimationFrame(() => {
                        handle = undefined;
                    });
                });
            }
        };

        window.addEventListener('resize', handler);

        handler();

        return () => {
            window.removeEventListener('resize', handler);
            if (handle !== undefined) {
                cancelAnimationFrame(handle);
            }
        };
    });

    // don't flicker and render low availability form twice
    const showSlots = selectedDate !== '';
    const intl = useIntl();
    const selectSlotAndGoNext = React.useCallback(
        (time: string, isDigitalWalkIn?: boolean) => {
            if (selectedDate) {
                startBooking({
                    citySlug,
                    locationSlug,
                    locationId: location._id,
                    date: selectedDate,
                    time,
                    provider: provider ?? { state: 'any' },
                    isDigitalWalkIn,
                });
            }
        },
        [
            citySlug,
            location._id,
            locationSlug,
            provider,
            selectedDate,
            startBooking,
        ],
    );

    // useLazyQuery sets loading to false until it starts executing
    // add a small delay to prevent the non-placeholder version flashing
    const [isDelayElapsed, setIsDelayElapsed] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(true);

    React.useEffect(() => {
        const timer = setTimeout(() => {
            setIsDelayElapsed(true);
        }, 200);

        return () => clearTimeout(timer);
    }, []);

    React.useEffect(() => {
        setIsLoading(!isDelayElapsed || isLoadingDates || isLoadingSlots);
    }, [isDelayElapsed, isLoadingDates, isLoadingSlots]);

    React.useEffect(() => {
        if (onLoadingChange) {
            onLoadingChange(isLoading);
        }
    }, [isLoading, onLoadingChange]);

    const numberOfUngroupedSlots =
        ungroupedSlots?.length ?? THRESHOLD_LOW_AVAILABILITY + 1;
    const showHeader = numberOfUngroupedSlots === 0;

    // digital walk in logic
    const digitalWalkInAvailability = useDigitalWalkInAvailability(locationId);
    const [digitalWalkInModalOpen, setDigitalWalkInModalOpen] =
        React.useState(false);
    const hasSelectedProvider = Boolean(provider?.id);

    const shouldDisplayWalkInComponent = useShouldShowDigitalWalkIn(
        digitalWalkInAvailability,
        setDigitalWalkInModalOpen !== undefined,
        isToday,
        ungroupedSlots?.length ?? 0,
        hasSelectedProvider,
    );

    let slotsContent;

    if (!isLoading && !hasSelectedProvider && fullyBooked) {
        slotsContent = (
            <NoAvailability
                {...{
                    location,
                    ungroupedSlots,
                    selectSlotAndGoNext,
                    selectedSlot,
                    selectedDate,
                    setSelectedNearbySlot,
                    isToday,
                    isClosed,
                    showHeader,
                    shouldDisplayWalkInComponent,
                    setDigitalWalkInModalOpen,
                }}
            />
        );
    } else if (
        showSlots &&
        !isLoadingDates &&
        !isLoadingSlots &&
        ungroupedSlots?.length
    ) {
        slotsContent = (
            <>
                <div className={styles.slots}>
                    <h4 className={styles.slotsHeader}>
                        {isToday &&
                            `${intl.formatMessage({ id: 'booking.form.date.today' })}, `}
                        {selectedDateMMMMDYYYY}
                    </h4>
                    <SlotButtons
                        slots={ungroupedSlots}
                        selectedSlot={selectedSlot}
                        onSelect={selectSlotAndGoNext}
                    />
                </div>

                {shouldDisplayWalkInComponent && (
                    <div className={styles.digitalWalkInWrapper}>
                        <DigitalWalkInBooking
                            setDigitalWalkInModalOpen={
                                setDigitalWalkInModalOpen
                            }
                            eventPrefix="cdp"
                            classes={{ root: styles.digitalWalkIn }}
                        />
                    </div>
                )}
            </>
        );
    } else if (hasSelectedProvider && allSlots?.length === 0) {
        slotsContent = (
            <NoAvailability
                {...{
                    location,
                    ungroupedSlots,
                    selectSlotAndGoNext,
                    selectedSlot,
                    selectedDate,
                    setSelectedNearbySlot,
                    isToday,
                    isClosed,
                    showHeader,
                    shouldDisplayWalkInComponent,
                    setDigitalWalkInModalOpen,
                }}
            />
        );
    } else {
        slotsContent = (
            <>
                <div className={styles.slots}>
                    {!isLoading && allowIndividualProviderBookings && (
                        <>
                            <h4 className={styles.noAvailabilityHeader}>
                                {intl.formatMessage({
                                    id: 'booking.form.date.noAppointmentsAvailable',
                                })}
                            </h4>
                            <p className={styles.noAvailability}>
                                {intl.formatMessage(
                                    {
                                        // eslint-disable-next-line max-len
                                        id: 'booking.form.date.noIndividualProviderSlotsAvailableText',
                                    },
                                    {
                                        provider:
                                            provider && 'name' in provider
                                                ? provider?.name?.replace(
                                                      REPLACE_DR_PREFIX_REGEX,
                                                      '',
                                                  )
                                                : '',
                                        location: location?.name,
                                        strong: ((chunks: React.ReactNode) => (
                                            <strong>{chunks}</strong>
                                        )) as unknown as React.ReactNode,
                                    },
                                )}
                            </p>
                            <p className={styles.noAvailability}>
                                <strong>
                                    {intl.formatMessage({
                                        id: 'booking.form.date.showingAllProviders',
                                    })}
                                </strong>
                            </p>
                        </>
                    )}
                    <SlotButtons
                        slots={allSlots}
                        selectedSlot={selectedSlot}
                        onSelect={(time: string) =>
                            allowIndividualProviderBookings
                                ? setSelectedNearbySlot({
                                      locationId: location._id,
                                      locationName: location.name,
                                      time,
                                      provider: true,
                                      citySlug,
                                      locationSlug,
                                  })
                                : selectSlotAndGoNext(time)
                        }
                    />
                </div>
                {shouldDisplayWalkInComponent && (
                    <div className={styles.digitalWalkInWrapper}>
                        <DigitalWalkInBooking
                            setDigitalWalkInModalOpen={
                                setDigitalWalkInModalOpen
                            }
                            eventPrefix="cdp"
                            classes={{ root: styles.digitalWalkIn }}
                        />
                    </div>
                )}
            </>
        );
    }

    const onSelectDate = React.useCallback(
        (date: string) => {
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    // clear selected slot, if date changed
                    if (selectedDate !== date && selectedSlot) {
                        selectSlot('');
                    }

                    selectDate(date);
                });
            });
        },
        [selectedDate, selectedSlot, selectSlot, selectDate],
    );

    const loadNearbySlots = !isLoading && showSlots && fullyBooked;

    const providersButtons = (
        <div className={styles.providers}>
            <h4>
                {intl.formatMessage({
                    id: 'booking.locationForm.providersTitle',
                })}
            </h4>
            <div className={styles.providerScrollWrapper}>
                {!isAtStart && (
                    <div
                        className={classnames(
                            styles.providerScrollButton,
                            styles.providerScrollBackButton,
                        )}
                    >
                        <button
                            type="button"
                            onClick={() => scrollToLast()}
                            aria-label={intl.formatMessage({
                                id: 'booking.locationForm.scrollToLast',
                            })}
                        >
                            <ChevronLeft />
                        </button>
                    </div>
                )}
                <div className={styles.providerButtons} ref={providersRef}>
                    <button
                        type="button"
                        className={classnames({
                            [styles.isSelected]: !provider?.id,
                        })}
                        onClick={() => {
                            setProvider({ state: 'any' });
                            scroll(0);
                        }}
                    >
                        <div>
                            {intl.formatMessage({
                                id: 'booking.locationForm.allProviders',
                            })}
                        </div>
                    </button>
                    {filteredOutPlaceholdersFromProviders?.map(
                        (thisProvider, i) => (
                            <Provider
                                key={thisProvider.id}
                                provider={thisProvider}
                                selected={thisProvider.id === provider?.id}
                                setProvider={(p) => {
                                    setProvider(p);
                                    selectSlot('');
                                    scroll(i + 1);
                                }}
                                classes={{
                                    isSelected: styles.isSelected,
                                }}
                            />
                        ),
                    )}
                </div>
                {!isAtEnd && (
                    <div
                        className={classnames(
                            styles.providerScrollButton,
                            styles.providerScrollForwardButton,
                        )}
                    >
                        <button
                            type="button"
                            onClick={() => scrollToNext()}
                            aria-label={intl.formatMessage({
                                id: 'booking.locationForm.scrollToNext',
                            })}
                        >
                            <ChevronRight />
                        </button>
                    </div>
                )}
            </div>
        </div>
    );

    const handleNearbySubmit = React.useCallback(() => {
        if (selectedNearbySlot) {
            startBooking({
                locationId: selectedNearbySlot.locationId,
                date: selectedDate,
                time: selectedNearbySlot.time,
                locationSlug: selectedNearbySlot.locationSlug,
                citySlug: selectedNearbySlot.citySlug,
                provider: { state: 'any' },
            });
        }
    }, [selectedNearbySlot, startBooking, selectedDate]);

    React.useEffect(() => {
        if (
            !isEqual(
                weekDays.map((day) => day?.date),
                lastWeekDays.map((day) => day?.date),
            )
        ) {
            setLastWeekDays(weekDays);
        }
    }, [weekDays, lastWeekDays]);

    return (
        <NearbySlotsProvider
            nearbyLocations={nearbyLocations}
            day={selectedDate}
            loadSlots={loadNearbySlots}
        >
            <div className={styles.root}>
                <div className={styles.wrapper} ref={wrapperRef}>
                    <div className={styles.title}>
                        <h4>
                            {intl.formatMessage({
                                id: 'booking.locationForm.title',
                            })}
                        </h4>
                        <h3>{location?.name}</h3>
                    </div>
                    <div className={styles.grid}>
                        {allowIndividualProviderBookings && providersButtons}
                        <div className={styles.calendar} ref={calendar}>
                            <header>
                                {hasPreviousWeek && (
                                    <button
                                        type="button"
                                        onClick={previousWeek}
                                        className={styles.previous}
                                    >
                                        <span>Go to previous week</span>
                                        <ChevronLeft />
                                    </button>
                                )}
                                {isThisWeek && <h4>This Week</h4>}
                                {isNextWeek && <h4>Next Week</h4>}
                                {!isThisWeek && !isNextWeek && (
                                    <h4>
                                        {week.format('MMM D')} -{' '}
                                        {week
                                            .clone()
                                            .add(6, 'days')
                                            .format('MMM D')}
                                    </h4>
                                )}
                                {hasNextWeek && (
                                    <button
                                        type="button"
                                        onClick={nextWeek}
                                        className={styles.next}
                                    >
                                        <span>Go to next week</span>
                                        <ChevronRight />
                                    </button>
                                )}
                            </header>
                            {isLoadingDates && (
                                <div className={styles.loading}>
                                    <CircularProgress
                                        size={100}
                                        color="secondary"
                                        aria-label={intl.formatMessage({
                                            id: 'booking.locationForm.loadingDates',
                                        })}
                                    />
                                </div>
                            )}
                            <div className={styles.dates}>
                                <div className={styles.daysOfTheWeek}>
                                    {daysOfTheWeek.map((weekDay) => (
                                        <strong key={weekDay}>{weekDay}</strong>
                                    ))}
                                </div>
                                <div className={styles.weekDays}>
                                    <div className={styles.thisWeekDays}>
                                        {weekDays.map((day, idx) => (
                                            <Day
                                                key={`${
                                                    day?.date || ''
                                                }-${idx.toString(10)}`}
                                                day={day}
                                                onSelect={onSelectDate}
                                                classes={{
                                                    isSelected:
                                                        styles.isSelected,
                                                    isToday: styles.isToday,
                                                }}
                                            />
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                        {isLoadingSlots && (
                            <div className={styles.slots}>
                                <div className={styles.loading}>
                                    <CircularProgress
                                        size={100}
                                        color="secondary"
                                        aria-label={intl.formatMessage({
                                            id: 'booking.locationForm.loadingSlots',
                                        })}
                                    />
                                </div>
                            </div>
                        )}
                        {!isLoadingSlots && slotsContent}
                    </div>
                </div>
                <ConfirmModal
                    showModal={!!selectedNearbySlot}
                    hideModal={() => setSelectedNearbySlot(null)}
                    handleSubmit={handleNearbySubmit}
                    selectedSlot={selectedNearbySlot}
                    timezone={timezone}
                />
                <DigitalWalkInModal
                    digitalWalkInSlots={digitalWalkInAvailability.data}
                    showModal={digitalWalkInModalOpen}
                    handleClose={() => setDigitalWalkInModalOpen(false)}
                    handleSubmit={(time: string) => {
                        selectSlotAndGoNext(time, true);
                    }}
                    eventPrefix="cdp"
                />
            </div>
        </NearbySlotsProvider>
    );
}
